// Follow up on (spec C6, S15 batch 6a; the Record and Renewals boards, kit `hbox` and `qbox`): the
// AE types the day they come back to a handoff with them. In the Record banner the box carries its
// own Clear, Cancel and Set; in the Quick view the box sits over the footer, which shows them instead.
import { useState } from 'react';
import { followUpHelp, followUpInput, parseFollowUp } from './ae-handoff-lines';

type Props = {
  /** The day already set, if any (YYYY-MM-DD). */
  day: string | null;
  now: Date;
  onCancel: () => void;
  /** True once saved; why not, otherwise. */
  onSave: (on: string | null) => Promise<true | string>;
};

function useFollowUpForm({ day, now, onCancel, onSave }: Props) {
  const [text, setText] = useState(() => followUpInput(day));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const parsed = parseFollowUp(text, now);
  const valid = !!parsed && 'day' in parsed;
  const save = async (on: string | null) => {
    // The same day again changes nothing: the box just closes.
    if (on === day) return onCancel();
    setBusy(true);
    const result = await onSave(on);
    setBusy(false);
    if (result !== true) setError(result);
  };
  const input = (
    <div>
      <label htmlFor="fu-date" className="flab">
        Follow up on
      </label>
      <input
        id="fu-date"
        className="input"
        type="text"
        placeholder="Nov 14, 2026"
        autoComplete="off"
        // A popup over the page owns Enter; here it sets the day.
        onKeyDown={(e) => {
          if (e.key === 'Enter' && valid && !busy) void save(parsed.day);
        }}
        aria-invalid={parsed && 'error' in parsed ? 'true' : 'false'}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setError(null);
        }}
      />
    </div>
  );
  const problem = error ?? (parsed && 'error' in parsed ? parsed.error : null);
  const errorLine = problem && (
    <p className="ferr" role="alert">
      {problem}
    </p>
  );
  /** Clear, for a day already set: the box's own size of button. */
  const clear = (cls: string) =>
    day ? (
      <button type="button" className={cls} disabled={busy} onClick={() => void save(null)}>
        Clear
      </button>
    ) : null;
  return { input, errorLine, clear, valid, busy, set: () => valid && void save(parsed.day) };
}

/** The box in the AE's Record banner, with its own buttons. */
export function FollowUpBox(props: Props) {
  const f = useFollowUpForm(props);
  return (
    <div className="hbox">
      <div className="row g12 wrap">
        {f.input}
        <span className="t12 muted grow">{followUpHelp.record}</span>
        {f.clear('btn btn-sm')}
        <button type="button" className="btn btn-sm" onClick={props.onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className={f.valid ? 'btn btn-sm btn-primary' : 'btn btn-sm'}
          disabled={!f.valid || f.busy}
          onClick={f.set}
        >
          Set
        </button>
      </div>
      {f.errorLine}
    </div>
  );
}

/** The Quick view's box over its footer, and the footer's buttons while it is open. */
export function FollowUpFooter(props: Props) {
  const f = useFollowUpForm(props);
  return (
    <>
      <div className="qbox">
        <div className="row g12 wrap">
          {f.input}
          <span className="t12 muted grow">{followUpHelp.quick}</span>
        </div>
        {f.errorLine}
      </div>
      <div className="p-foot">
        <button type="button" className="btn" onClick={props.onCancel}>
          Cancel
        </button>
        {f.clear('btn')}
        <button
          type="button"
          className={f.valid ? 'btn btn-primary ml-auto' : 'btn ml-auto'}
          disabled={!f.valid || f.busy}
          onClick={f.set}
        >
          Set
        </button>
      </div>
    </>
  );
}
